import React, { useEffect, useState } from 'react';
import axios from 'axios';
import InvoiceView from './InvoiceView';
import './InvoiceDocList.css';

export default function InvoiceDocList() {
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [locations, setLocations] = useState([]); // Nouvel état pour les localisations
  const [selectedLocation, setSelectedLocation] = useState('Toutes'); // Filtre localisation

  const [totals, setTotals] = useState({
    totalHT: 0,
    totalTVA: 0,
    totalTTC: 0
  });

  const [selectedYear, setSelectedYear] = useState('Toutes');
  const [selectedMonth, setSelectedMonth] = useState('Tous');

  // Fonction pour récupérer les localisations
 // InvoiceDocList.js - Modifier la fonction fetchLocations
const fetchLocations = async () => {
  try {
    const response = await axios.get('/api/Locations/getAllLocations');
    if (response.data) {
      setLocations(response.data);
    }
  } catch (err) {
    console.error('Erreur récupération localisations:', err);
    // Si l'URL relative échoue, essayer avec l'URL complète
    try {
      const response = await axios.get('/api/Locations/getAllLocations');
      if (response.data) {
        setLocations(response.data);
      }
    } catch (secondErr) {
      console.error('Échec total récupération localisations:', secondErr);
      // Option: créer des localisations par défaut
      setLocations([
        { _id: 'default1', name: 'Magasin Principal' },
        { _id: 'default2', name: 'Succursale Nord' },
        { _id: 'default3', name: 'Succursale Sud' }
      ]);
    }
  }
};

  const getAvailableYearsMonths = (invoicesList) => {
    const years = new Set();
    const months = new Set();
    
    invoicesList.forEach(invoice => {
      if (invoice.date) {
        try {
          const date = new Date(invoice.date);
          if (!isNaN(date.getTime())) {
            years.add(date.getFullYear());
            months.add(date.getMonth() + 1);
          }
        } catch (error) {
          console.warn('Date invalide:', invoice.date);
        }
      }
    });
    
    return {
      years: ['Toutes', ...Array.from(years).sort((a, b) => b - a)],
      months: ['Tous', ...Array.from(months).sort((a, b) => a - b)]
    };
  };

  const filterInvoices = (invoicesList, year, month, locationId) => {
    return invoicesList.filter(invoice => {
      if (!invoice.date) return false;
      
      try {
        const invoiceDate = new Date(invoice.date);
        if (isNaN(invoiceDate.getTime())) return false;
        
        const invoiceYear = invoiceDate.getFullYear();
        const invoiceMonth = invoiceDate.getMonth() + 1;
        
        const yearMatch = year === 'Toutes' || invoiceYear === parseInt(year);
        const monthMatch = month === 'Tous' || invoiceMonth === parseInt(month);
        const locationMatch = locationId === 'Toutes' || invoice.location === locationId;
        
        return yearMatch && monthMatch && locationMatch;
      } catch (error) {
        console.warn('Erreur de filtrage pour la facture:', invoice._id);
        return false;
      }
    });
  };

  const calculateTotals = (invoicesList) => {
    const calculatedTotals = invoicesList.reduce((acc, invoice) => {
      let taxAmount = 0;
      if (invoice.taxAmount !== undefined && invoice.taxAmount !== null) {
        taxAmount = invoice.taxAmount;
      } else if (invoice.totalTTC && invoice.subtotalHT) {
        taxAmount = invoice.totalTTC - invoice.subtotalHT;
      }
      
      const subtotalHT = Number(invoice.subtotalHT) || 0;
      const totalTTC = Number(invoice.totalTTC) || 0;
      
      return {
        totalHT: acc.totalHT + subtotalHT,
        totalTVA: acc.totalTVA + taxAmount,
        totalTTC: acc.totalTTC + totalTTC
      };
    }, { totalHT: 0, totalTVA: 0, totalTTC: 0 });

    setTotals(calculatedTotals);
  };

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      setError(null);
      
      let response;
      try {
        response = await axios.get('/api/invoices');
      } catch (relativeError) {
        console.log('URL relative échoue, tentative avec URL complète...');
        response = await axios.get('/api/invoices');
      }
      
      if (response.data && Array.isArray(response.data)) {
        setInvoices(response.data);
        
        const filtered = filterInvoices(response.data, selectedYear, selectedMonth, selectedLocation);
        setFilteredInvoices(filtered);
        calculateTotals(filtered);
      } else {
        throw new Error('Format de réponse invalide');
      }
      
    } catch (err) {
      console.error('❌ Erreur fetch:', err);
      const errorMessage = err.response?.data?.message || 
                          err.message || 
                          'Erreur lors du chargement des factures. Vérifiez que le serveur backend est démarré.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
    fetchLocations(); // Charger les localisations
  }, []);

  useEffect(() => {
    if (invoices.length > 0) {
      const filtered = filterInvoices(invoices, selectedYear, selectedMonth, selectedLocation);
      setFilteredInvoices(filtered);
      calculateTotals(filtered);
    }
  }, [selectedYear, selectedMonth, selectedLocation, invoices]);

  const archiveFilteredInvoices = async () => {
    try {
      const now = new Date();
      const filterInfo = selectedYear !== 'Toutes' ? `_${selectedYear}` : '';
      const filterInfoMonth = selectedMonth !== 'Tous' ? `_mois${selectedMonth}` : '';
      const locationInfo = selectedLocation !== 'Toutes' ? `_${locations.find(l => l._id === selectedLocation)?.name || 'localisation'}` : '';
      
      const archiveData = {
        fileName: `Factures${filterInfo}${filterInfoMonth}${locationInfo}_${now.toISOString().split('T')[0]}.json`,
        archiveDate: now.toISOString(),
        totalHT: totals.totalHT,
        totalTVA: totals.totalTVA,
        totalTTC: totals.totalTTC,
        invoiceCount: filteredInvoices.length,
        filter: {
          year: selectedYear,
          month: selectedMonth,
          location: selectedLocation
        },
        invoices: filteredInvoices.map(invoice => ({
          _id: invoice._id,
          supplierName: invoice.supplierName,
          invoiceNumber: invoice.invoiceNumber,
          date: invoice.date,
          subtotalHT: invoice.subtotalHT,
          totalTTC: invoice.totalTTC,
          taxRate: invoice.taxRate,
          currency: invoice.currency,
          status: invoice.status,
          location: invoice.location,
          locationName: invoice.locationName
        }))
      };

      const dataStr = JSON.stringify(archiveData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = archiveData.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      alert(`✅ ${filteredInvoices.length} facture(s) archivée(s) avec succès !`);

    } catch (error) {
      console.error('Erreur lors de l\'archivage:', error);
      alert('❌ Erreur lors de l\'archivage: ' + error.message);
    }
  };

  const formatCurrency = (amount, currency = 'TND') => {
    if (amount == null || amount === 0 || isNaN(amount)) return '-';
    
    try {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: currency === 'DT' ? 'TND' : currency,
        minimumFractionDigits: 3,
        maximumFractionDigits: 3
      }).format(amount);
    } catch (error) {
      return `${Number(amount).toFixed(3)} ${currency === 'DT' ? 'TND' : currency}`;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return isNaN(date.getTime()) ? '-' : date.toLocaleDateString('fr-FR');
    } catch (error) {
      return '-';
    }
  };

  const calculateTaxAmount = (invoice) => {
    if (invoice.taxAmount !== undefined && invoice.taxAmount !== null) {
      return invoice.taxAmount;
    }
    if (invoice.totalTTC && invoice.subtotalHT) {
      return invoice.totalTTC - invoice.subtotalHT;
    }
    return 0;
  };

  const getStatusInfo = (invoice) => {
    if (invoice.status === 'confirmed') {
      return { color: '#38a169', text: '✅ Confirmée' };
    }
    if (invoice.status === 'pending') {
      return { color: '#d69e2e', text: '⏳ En attente' };
    }
    
    if (!invoice.totalTTC && !invoice.subtotalHT) {
      return { color: '#e53e3e', text: '❌ Données manquantes' };
    }
    if (invoice.totalTTC > 0 && invoice.subtotalHT > 0) {
      return { color: '#38a169', text: '✅ Données complètes' };
    }
    return { color: '#d69e2e', text: '⚠️ Données partielles' };
  };

  const handleViewInvoice = (invoice) => {
    setSelectedInvoice(invoice);
  };

  const handleCloseView = () => {
    setSelectedInvoice(null);
  };

  const handleUpdateInvoices = () => {
    fetchInvoices();
    handleCloseView();
  };

  const { years, months } = getAvailableYearsMonths(invoices);

  const monthNames = {
    1: 'Janvier', 2: 'Février', 3: 'Mars', 4: 'Avril', 5: 'Mai', 6: 'Juin',
    7: 'Juillet', 8: 'Août', 9: 'Septembre', 10: 'Octobre', 11: 'Novembre', 12: 'Décembre'
  };

  if (loading) {
    return (
      <div className="invoice-doc-list">
        <div className="invoice-loading">
          <div className="loading-spinner"></div>
          <p>Chargement des factures...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="invoice-doc-list">
        <div className="invoice-error">
          <div className="error-icon">⚠️</div>
          <h3>Erreur de chargement</h3>
          <p>{error}</p>
          <button onClick={fetchInvoices} className="retry-btn">
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="invoice-doc-list">
      {/* En-tête */}
      <div className="invoice-header">
        <div className="header-content">
          <h1 className="invoice-title">
            <span className="title-icon">📋</span>
            Gestion des Factures
            {selectedYear !== 'Toutes' && ` - ${selectedYear}`}
            {selectedMonth !== 'Tous' && ` - ${monthNames[selectedMonth]}`}
            {selectedLocation !== 'Toutes' && ` - ${locations.find(l => l._id === selectedLocation)?.name || 'Localisation'}`}
          </h1>
          <p className="invoice-subtitle">
            {filteredInvoices.length} facture{filteredInvoices.length !== 1 ? 's' : ''} • 
            Total HT: <strong>{formatCurrency(totals.totalHT)}</strong> • 
            Total TVA: <strong>{formatCurrency(totals.totalTVA)}</strong> • 
            Total TTC: <strong>{formatCurrency(totals.totalTTC)}</strong>
          </p>
        </div>
        <div className="header-actions">
          <button onClick={fetchInvoices} className="square-btn refresh-btn" title="Actualiser la liste">
            <span className="btn-icon">🔄</span>
            Actualiser
          </button>
          {filteredInvoices.length > 0 && (
            <button onClick={archiveFilteredInvoices} className="square-btn archive-btn" title="Télécharger l'archive">
              <span className="btn-icon">💾</span>
              Archiver ({filteredInvoices.length})
            </button>
          )}
        </div>
      </div>

      {/* Filtres */}
      <div className="filters-container">
        <div className="filter-group">
          <label className="filter-label">Filtrer par année :</label>
          <div className="filter-buttons">
            {years.map(year => (
              <button
                key={year}
                onClick={() => setSelectedYear(year)}
                className={`square-btn filter-btn ${selectedYear === year ? 'active' : ''}`}
              >
                {year}
              </button>
            ))}
          </div>
        </div>
        
        {selectedYear !== 'Toutes' && (
          <div className="filter-group">
            <label className="filter-label">Filtrer par mois :</label>
            <div className="filter-buttons">
              {months.map(month => (
                <button
                  key={month}
                  onClick={() => setSelectedMonth(month)}
                  className={`square-btn filter-btn ${selectedMonth === month ? 'active' : ''}`}
                >
                  {month === 'Tous' ? 'Tous les mois' : monthNames[month]}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Nouveau filtre par localisation */}
        <div className="filter-group">
          <label className="filter-label">Filtrer par localisation :</label>
          <div className="filter-buttons">
            <button
              onClick={() => setSelectedLocation('Toutes')}
              className={`square-btn filter-btn ${selectedLocation === 'Toutes' ? 'active' : ''}`}
            >
              Toutes les localisations
            </button>
            {locations.map(location => (
              <button
                key={location._id}
                onClick={() => setSelectedLocation(location._id)}
                className={`square-btn filter-btn ${selectedLocation === location._id ? 'active' : ''}`}
              >
                {location.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tableau des factures */}
      {filteredInvoices.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📄</div>
          <h3>Aucune facture trouvée</h3>
          <p>
            {invoices.length === 0 
              ? "Aucune facture n'a été importée pour le moment" 
              : "Aucune facture ne correspond aux filtres sélectionnés"
            }
          </p>
          <button 
            onClick={() => {
              setSelectedYear('Toutes');
              setSelectedMonth('Tous');
              setSelectedLocation('Toutes');
            }} 
            className="square-btn retry-btn"
          >
            {invoices.length === 0 ? 'Importer une facture' : 'Voir toutes les factures'}
          </button>
        </div>
      ) : (
        <div className="table-wrapper">
          <div className="table-container">
            <table className="invoice-table">
              <thead>
                <tr>
                  <th>Fournisseur</th>
                  <th>N° Facture</th>
                  <th>Date</th>
                  <th>Localisation</th>
                  <th>Montant HT</th>
                  <th>Montant TVA</th>
                  <th>Montant TTC</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.map((invoice) => {
                  const statusInfo = getStatusInfo(invoice);
                  return (
                    <tr key={invoice._id} className="invoice-row">
                      <td className="supplier-cell">
                        <div className="supplier-info">
                          <span className="supplier-name">
                            {invoice.supplierName || 'Non spécifié'}
                          </span>
                          {invoice.ocrEngine === 'mistral' && (
                            <span className="ai-indicator" title="Traité par IA">🤖</span>
                          )}
                        </div>
                      </td>
                      <td className="invoice-number">
                        <span className="invoice-number-text">
                          {invoice.invoiceNumber || '-'}
                        </span>
                      </td>
                      <td className="date-cell">
                        {formatDate(invoice.date)}
                      </td>
                      <td className="location-cell">
                        <span className={`location-badge ${!invoice.locationName ? 'no-location' : ''}`}>
                          {invoice.locationName || 'Non affectée'}
                        </span>
                      </td>
                      <td className="amount-cell">
                        {formatCurrency(invoice.subtotalHT, invoice.currency)}
                      </td>
                      <td className="amount-cell">
                        {formatCurrency(calculateTaxAmount(invoice), invoice.currency)}
                      </td>
                      <td className="amount-cell total-ttc">
                        {formatCurrency(invoice.totalTTC, invoice.currency)}
                      </td>
                      <td className="status-cell">
                        <span 
                          className="status-badge"
                          style={{ backgroundColor: statusInfo.color }}
                          title={statusInfo.text}
                        >
                          {statusInfo.text}
                        </span>
                      </td>
                      <td className="actions-cell">
                        <button 
                          onClick={() => handleViewInvoice(invoice)}
                          className="square-btn view-btn"
                          title="Voir et modifier la facture"
                        >
                          <span className="btn-icon">👁️</span>
                          Voir
                        </button>
                      </td>
                    </tr>
                  );
                })}
                
                {/* Ligne des totaux */}
                <tr className="totals-row">
                  <td colSpan="4" className="totals-label">
                    <strong>
                      TOTAUX 
                      {selectedYear !== 'Toutes' && ` ${selectedYear}`}
                      {selectedMonth !== 'Tous' && ` - ${monthNames[selectedMonth]}`}
                      {selectedLocation !== 'Toutes' && ` - ${locations.find(l => l._id === selectedLocation)?.name || 'Localisation'}`}
                    </strong>
                  </td>
                  <td className="total-amount">
                    <strong>{formatCurrency(totals.totalHT)}</strong>
                  </td>
                  <td className="total-amount">
                    <strong>{formatCurrency(totals.totalTVA)}</strong>
                  </td>
                  <td className="total-amount total-ttc">
                    <strong>{formatCurrency(totals.totalTTC)}</strong>
                  </td>
                  <td colSpan="2">
                    <span className="invoice-count">
                      {filteredInvoices.length} facture(s)
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal de détail */}
      {selectedInvoice && (
        <div className="modal-overlay" onClick={handleCloseView}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                <span className="modal-icon">📄</span>
                Détails de la facture
                {selectedInvoice.ocrEngine === 'mistral' && (
                  <span className="modal-ai-badge" title="Traité par Mistral AI">AI</span>
                )}
              </h2>
              <button className="square-btn close-btn" onClick={handleCloseView} title="Fermer">
                ✕
              </button>
            </div>
            <div className="modal-body">
              <InvoiceView 
                id={selectedInvoice._id} 
                invoice={selectedInvoice}
                onClose={handleCloseView}
                onUpdate={handleUpdateInvoices}
                locations={locations} // Passer les localisations au composant enfant
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}